"use strict";
/**
 * The operator's tap pad vocabulary (ai/TAP_PAD_PLAN.md): the T2 level from the
 * tap-vocabulary study, plus the play frame (SNAP ... DONE), yardage, the try
 * after a touchdown, FLAG and UNDO. Shared by the pad and the server validator so the two can never
 * disagree on a kind.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_TAPS_PER_BATCH = exports.isTapLeague = exports.TAP_LEAGUES = exports.NBA_CLOCK_SOURCES = exports.NBA_NO_SNAPSHOT_KINDS = exports.NBA_CLOCK_KINDS = exports.NBA_TRACK_KINDS = exports.NBA_SIDED_TAP_KINDS = exports.NBA_UNSIDED_TAP_KINDS = exports.NBA_ALL_KINDS = exports.nbaPadVersion = exports.NBA_ACCEPTED_PAD_VERSIONS = exports.NBA_PAD_VERSIONS = exports.NBA_KINDS_V3 = exports.NBA_KINDS_V2 = exports.NBA_TAP_KINDS = exports.isTapKind = exports.YARD_BUCKETS = exports.SIDED_TAP_KINDS = exports.TAP_KINDS = void 0;
exports.nbaKindsFor = nbaKindsFor;
exports.tapVocabulary = tapVocabulary;
exports.TAP_KINDS = [
    "snap",
    "done",
    "yards",
    "touchdown",
    "field_goal",
    "safety",
    // The try after a touchdown; its team is the last touchdown's side.
    "go_two",
    "xp_good",
    "xp_miss",
    "two_good",
    "two_fail",
    "turnover",
    "punt",
    "fg_missed",
    "downs",
    "first_down",
    "gain_short",
    "no_gain",
    "incomplete",
    "sack",
    "flag",
    "pull",
    "undo",
];
/** Kinds that must say which team; everything else is read off the pre-snap possession. */
exports.SIDED_TAP_KINDS = new Set(["touchdown"]);
/** Total yards from scrimmage on the play, as the `yards` tap's value. */
exports.YARD_BUCKETS = [-5, 10, 20, 40];
const isTapKind = (v) => typeof v === "string" && exports.TAP_KINDS.includes(v);
exports.isTapKind = isTapKind;
/**
 * The NBA recorder's vocabulary (ai/nba/N8-taps/PLAN.md §2, §10): W1's window events, each sided
 * by the half of the pad tapped; free-throw trip starts (the kind carries the attempts awarded)
 * and FT ?; a technical's one awarded attempt and its explicit result (§12.6); plus PULL QUOTES,
 * UNDO, the pad's own session_start, and the tracking presses (§16). No play frame.
 */
exports.NBA_TAP_KINDS = [
    "made_2",
    "made_3",
    "ft_made",
    "ft_missed",
    "turnover",
    "def_rebound",
    "ft_trip_1",
    "ft_trip_2",
    "ft_trip_3",
    "ft_unsure",
    "ft_tech",
    "ft_tech_made",
    "ft_tech_missed",
    "pull",
    "undo",
    "session_start",
    "track_start",
    "track_stop",
];
/**
 * Pad version 2, layout E+M (N8 §19, §19a): two-step shots, Missed, one Ball button tapped on the
 * side that gains the ball, Off Reb, the period, PULL's latch and the operator's tip answer.
 * Retired from v1: def_rebound, turnover, ft_unsure.
 */
exports.NBA_KINDS_V2 = [
    "shot_2",
    "shot_3",
    "made_2",
    "made_3",
    "missed",
    "ball",
    "off_rebound",
    "ft_made",
    "ft_missed",
    "ft_trip_1",
    "ft_trip_2",
    "ft_trip_3",
    "ft_tech",
    "ft_tech_made",
    "ft_tech_missed",
    "period_next",
    "tip_confirm",
    "pull",
    "resume",
    "undo",
    "session_start",
    "track_start",
    "track_stop",
];
/** Pad version 3 (N8 §19.7): version 2 with the game clock. */
exports.NBA_KINDS_V3 = [...exports.NBA_KINDS_V2, "clock_start", "clock_stop", "clock_adjust"];
exports.NBA_PAD_VERSIONS = [1, 2, 3];
/** The versions the server stores. Version 3, the clock, is accepted from stage 4 (N8 §19.2). */
exports.NBA_ACCEPTED_PAD_VERSIONS = [1, 2];
/** Absent or null is version 1 everywhere, retry comparisons included (N8 §19a.6). */
const nbaPadVersion = (v) => v ?? 1;
exports.nbaPadVersion = nbaPadVersion;
/** A version's kinds; null for a version that does not exist. */
function nbaKindsFor(version) {
    return version === 1 ? exports.NBA_TAP_KINDS : version === 2 ? exports.NBA_KINDS_V2 : version === 3 ? exports.NBA_KINDS_V3 : null;
}
/** Every NBA kind of any version, for the shape check before a tap's version is known. */
exports.NBA_ALL_KINDS = [...new Set([...exports.NBA_TAP_KINDS, ...exports.NBA_KINDS_V3])];
/** Unsided NBA kinds, listed rather than derived, so a new kind must be placed deliberately. */
exports.NBA_UNSIDED_TAP_KINDS = new Set([
    "pull", "resume", "undo", "session_start", "track_start", "track_stop", "period_next", "tip_confirm",
    "clock_start", "clock_stop", "clock_adjust",
]);
exports.NBA_SIDED_TAP_KINDS = new Set(exports.NBA_ALL_KINDS.filter((k) => !exports.NBA_UNSIDED_TAP_KINDS.has(k)));
/** The tracking presses (N8 §16): unsided, never undone, never a play, never snapshotted. */
exports.NBA_TRACK_KINDS = new Set(["track_start", "track_stop"]);
/** The clock kinds (N8 §19.7, pad version 3). */
exports.NBA_CLOCK_KINDS = new Set(["clock_start", "clock_stop", "clock_adjust"]);
/**
 * Taps that owe no book snapshots (N8 §19.4): one set for the snapshotter, the export's accounting
 * and its verification. PULL keeps its snapshots, so exports taken before version 2 still verify.
 */
exports.NBA_NO_SNAPSHOT_KINDS = new Set([
    "session_start", "track_start", "track_stop", "resume", "period_next", "tip_confirm", "clock_start", "clock_stop", "clock_adjust",
]);
/** The clock record's sources (N8 §18a.3). */
exports.NBA_CLOCK_SOURCES = ["hand", "nudge", "typed", "espn", "auto"];
exports.TAP_LEAGUES = ["nfl", "ncaaf", "nba"];
const isTapLeague = (v) => typeof v === "string" && exports.TAP_LEAGUES.includes(v);
exports.isTapLeague = isTapLeague;
/** The league's kinds and which of them need a side. */
function tapVocabulary(league) {
    return league === "nba" ? { kinds: exports.NBA_ALL_KINDS, sided: exports.NBA_SIDED_TAP_KINDS } : { kinds: exports.TAP_KINDS, sided: exports.SIDED_TAP_KINDS };
}
exports.MAX_TAPS_PER_BATCH = 50;
